import 'bootstrap/dist/css/bootstrap.css';
import './components/design-system/components/components.css';
import './components/design-system/tokens/tokens.css';

import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { Provider } from 'react-redux';

import { AuthorizedApp } from './AuthorizedApp';
import { unregister as unregisterServiceWorker } from './registerServiceWorker';
import { ApplicationState } from './Store';
import configureStore from './Store/configureStore';
import { actionCreators as GlobalSettingActionCreators } from './Store/GlobalSettings';
import { defaultBrandConfig, TenantActionCreators } from './Store/Tenant';
import { defaultUserState } from './Store/User/Reducer';
import {
    GetTenantBasicSettings,
    GetTenantCSSConfig,
    GetTenantNameFromHostName
} from './Utils/BrandUtils';
import { assetBlobContainer } from './Utils/Constants';
import { fetchWrapper } from './Utils/FetchWrapper';


GetTenantNameFromHostName(window.location.hostname.toLowerCase())
    .then(tenantName => {
        GetTenantBasicSettings(tenantName).then(settings => {
            store.dispatch(TenantActionCreators.SetBasicTenantSettings(settings));
        });
        GetTenantCSSConfig(tenantName).then(brandConfig => {
            store.dispatch(TenantActionCreators.SetBrandConfig(brandConfig));
        });
    })

const initializationData: ApplicationState = {} as ApplicationState;

// basicSettings will get set above when it's retrieved.
initializationData.tenant = {
    tenantName: 'Data Extraction', // this will actually be set above when we get the tenant name
    tenantNameId: 'strongbox',
    basicSettings: undefined,
    brandConfig: defaultBrandConfig,
    emailDomains: [],
    userMetadataSettings: [],
    bannerMessages: [],
    dataCollectionLimits: undefined,
    featureSwitches: undefined,
};

initializationData.appSettings = {
    financialStatementsEnabled: false,
    financialStatementsReleaseEnabled: false,
    dynamicTenantSelection: false,
    shareableLinkParamsFlow: false,
    initialized: false,
    strongboxUrl: undefined,
    accountingPackages: [],
    accountingPkgBlackList: [],
    accountingPackageDefinitions: [],
    riskCalcEnabled: false,
    showDeletedSubmissions: false,
    strongboxSupportEmail: '',
    strongboxHelpCenterLink: '',
    maxYearsInAdditionToFYTDForCollection: 5,
    numMonthsInImportYTDDropdown: 7,
    disableAppInsights: false,
    disableDirectLinking: false,
    appInsightsInstrumentationKey: '',
    uploadModifiedWorkbooks: false,
    allowMultipleBorrowerImports: true,
    noTimeRestrictionForDataCollection: false,
    showBorrowerIntroBanner: false,
    alwaysCollectBorrowerBusinessInfo: false,
    disabledDataCollectionOptions: {
        disableAdditionalDocUpload: false,
        disableAnonymousDocUpload: false,
        disableProvideUserCopy: false,
        disableAnonymizeResults: false,
        disableBasisOfAccounting: false,
    },
    sessionMaxLifetimeInSeconds: 86400,   // one day
    allowExcelFileUpload: false,
    allowLanguageSelection: false,
    disableNewWorkspaces: false,
    consumerId: '',
    loginSessionSlidingLifetimeInSeconds: 1800, // 30 minutes in seconds
    isMultiTenant: false,
    disablePendo: false,
    dataCollectionRefactorEnabled: false,
    fieldGuideEnabled: false,
    supportsUniversalConnector: false,
};

initializationData.userState = {
    ...defaultUserState,
    auth0: {
        clientId: '',
        domain: '',
        scope: '',
        audience: '',
        redirectLogin: '',
    },
    auth0LoggingOut: false,
    auth0Token: undefined,
    auth0Id: undefined,
}

// TODO: Ultimately remove temporary code here that is used to set the date for when we show an error
// regarding a submission being too early to support mapping.  Setting this in blob storage for the time
// being so it's easily adjusted as we come down to release.

const minMappingConfigUrl = `${assetBlobContainer}/portalconfig/mapping.json`;

fetchWrapper(minMappingConfigUrl, {}, true, false)
    .then((response) => {
        if (response.ok) {
            response.json()
                .then((mappingSettings) => {
                    const dateString: string = mappingSettings['minMappingDate'];
                    const minMappingDate = new Date(dateString);
                    if (!isNaN(minMappingDate.valueOf())) {
                        store.dispatch(GlobalSettingActionCreators.InitializeMinMappingDate(minMappingDate));
                    }
                })
                .catch((responseException) => {
                    console.error(`Exception processing response from retrieving ${minMappingConfigUrl}`);
                    console.error(responseException);
                });
        }
    })
    .catch((e) => {
        console.error(`exception thrown attempting to retrieve ${minMappingConfigUrl}`);
        console.error(e);
    });

// Get the application-wide store instance, prepopulating with state from the server where available.
export const store = configureStore(initializationData);

const container = document.getElementById('root');
const root = ReactDOMClient.createRoot(container!);
root.render((
    <Provider store={store}>
        <AuthorizedApp />
    </Provider>
));

// disable service worker for now since it's impossible to override bad behavior
// see https://github.com/facebook/create-react-app/issues/5359
//registerServiceWorker();

unregisterServiceWorker();

